import axios from 'axios';
import { API_URL } from '../config/api';

/* Wspolne akcje (palety polecen + strona Raporty). */

export type ReportMode = 'now' | 'since' | 'range';

/**
 * Raport produktów do spakowania. Dane liczy UCS przez GET /v1/packages/report/* — TYM SAMYM
 * pipeline'em co kolejka (rozwiązanie daty przez włączony kontroler dat: today/bird), więc
 * raport jest poprawny niezależnie od kontrolera. Tryby:
 *  - now   → na teraz (dzisiaj + zaległe + bez terminu),
 *  - since → delta „nowe od ostatniego raportu" (serwerowy kursor),
 *  - range → okno dostaw [from, to] (YYYY-MM-DD).
 * `preview:true` = wydruk poglądowy: NIE przesuwa kursora `since` i znaczy raport „WYDRUK POGLĄDOWY".
 * `jobId` = zadanie druku (ekran drukowania stacji śledzi jego status); `empty` = w zakresie nie ma
 * zamówień, więc niczego nie drukowano (to nie błąd).
 */
export async function printProductsReport(opts?: {
  mode?: ReportMode;
  from?: string;
  to?: string;
  since?: string;
  preview?: boolean;
}): Promise<{ ok: boolean; message: string; jobId?: string | null; empty?: boolean }> {
  const assignments = (await axios.get(`${API_URL}/api/settings/printer-assignments`)).data;
  if (!assignments?.reports) {
    return { ok: false, message: 'Brak przypisanej drukarki dla raportów (Ustawienia → Drukarki)' };
  }
  const body: Record<string, unknown> = { printerName: assignments.reports, mode: opts?.mode || 'now' };
  if (opts?.from) body.from = opts.from;
  if (opts?.to) body.to = opts.to;
  if (opts?.since) body.since = opts.since;
  if (opts?.preview) body.preview = true;
  const r = await axios.post(`${API_URL}/api/printing/print-products-report`, body);
  const orders = typeof r.data?.ordersCount === 'number' ? r.data.ordersCount : null;
  if (r.data?.success && orders !== 0) {
    const tail = orders != null ? ` · ${orders} zam., ${r.data.totalItems ?? 0} szt.` : '';
    return { ok: true, message: (r.data.message || 'Raport wysłany do druku') + tail, jobId: r.data.jobId ?? null };
  }
  return { ok: false, message: r.data?.message || 'Brak zamówień do raportu w wybranym zakresie', empty: true };
}

/* Instalator agenta druku on-prem. Backend tworzy nowego agenta (bootstrap) i zwraca
   gotowy plik z wklejonym config.json (Windows: .exe stub+PS1 lub .ps1; mac/linux: .sh).
   Pobieramy jako blob (z auth admina), nazwę bierzemy z Content-Disposition. */
export async function downloadAgentInstaller(platform: 'win' | 'mac' | 'linux', name: string): Promise<void> {
  const r = await axios.get(`${API_URL}/api/agents/installer`, {
    params: { platform, name },
    responseType: 'blob',
  });
  const cd = String(r.headers['content-disposition'] || '');
  const m = cd.match(/filename="?([^"]+)"?/i);
  const fallback = platform === 'win'
    ? 'PackFlow-Agent-Setup.ps1'
    : `PackFlow-Agent-Setup-${platform === 'mac' ? 'macos' : 'linux'}.sh`;
  triggerDownload(r.data, m?.[1] || fallback);
}

function triggerDownload(blob: Blob, fileName: string) {
  const url = window.URL.createObjectURL(new Blob([blob]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
